﻿import * as yup from 'yup'
import type { SmartTravelInsuredPerson } from '../../dtos'

// číslo osoby z yup cesty "...insuredPersons[2].firstName" pro text chybové hlášky
const personLabel = (path: string) => {
    const match = path.match(/insuredPersons\[(\d+)\]/)

    return match ? `${parseInt(match[1]) + 1}. pojištěná osoba` : 'Pojištěná osoba'
}

// datum narození z rodného čísla (bez kontroly dělitelnosti - slouží jen pro posouzení věku)
export const getBirthdateFromBirthnumber = (birthnumber?: string | null): Date | null => {
    const digits = (birthnumber ?? '').replace(/\D/g, '')
    if (digits.length < 9 || digits.length > 10) return null

    let year = parseInt(digits.substring(0, 2))
    let month = parseInt(digits.substring(2, 4))
    const day = parseInt(digits.substring(4, 6))

    if (month > 70) month -= 70
    else if (month > 50) month -= 50
    else if (month > 20) month -= 20

    year += digits.length == 9 || year >= 54 ? 1900 : 2000

    const date = new Date(year, month - 1, day)
    if (isNaN(date.getTime()) || date.getMonth() != month - 1) return null

    return date
}

// osoby s vyplněným, ale neparsovatelným údajem posuzujeme jako dospělé (stejně jako server)
const isAdult = (person: Partial<SmartTravelInsuredPerson>) => {
    const birthdate = getBirthdateFromBirthnumber(person.birthnumber)
        ?? (person.birthdate ? new Date(person.birthdate) : null)

    if (!birthdate || isNaN(birthdate.getTime())) return true

    const now = new Date()
    let age = now.getFullYear() - birthdate.getFullYear()
    if (new Date(birthdate.getFullYear() + age, birthdate.getMonth(), birthdate.getDate()) > now) age--

    return age >= 26
}

const otherPersonCondition = (isPolicyHolder: boolean, isInsuredPerson: boolean) => !isPolicyHolder && !isInsuredPerson

export const SmartTravelInsuredPersonValidationSchema = yup.object().shape({
    firstName: yup.string().when(['isPolicyHolder', 'isInsuredPerson'], {
        is: otherPersonCondition,
        then: schema => schema.required(({ path }) => `${personLabel(path)}: zadejte jméno`),
        otherwise: schema => schema.nullable()
    }),
    lastName: yup.string().when(['isPolicyHolder', 'isInsuredPerson'], {
        is: otherPersonCondition,
        then: schema => schema.required(({ path }) => `${personLabel(path)}: zadejte příjmení`),
        otherwise: schema => schema.nullable()
    }),
    birthnumber: yup.string().when(['isPolicyHolder', 'isInsuredPerson', 'isForeigner'], {
        is: (isPolicyHolder: boolean, isInsuredPerson: boolean, isForeigner: boolean) =>
            otherPersonCondition(isPolicyHolder, isInsuredPerson) && !isForeigner,
        then: schema => schema
            .matches(/^\d*$/, ({ path }) => `${personLabel(path)}: rodné číslo musí obsahovat pouze číslice`)
            .required(({ path }) => `${personLabel(path)}: zadejte rodné číslo, případně označte, že rodné číslo nemá`),
        otherwise: schema => schema.nullable()
    }),
    nationalityFK: yup.string().when(['isPolicyHolder', 'isInsuredPerson', 'isForeigner'], {
        is: (isPolicyHolder: boolean, isInsuredPerson: boolean, isForeigner: boolean) =>
            otherPersonCondition(isPolicyHolder, isInsuredPerson) && !!isForeigner,
        then: schema => schema.required(({ path }) => `${personLabel(path)}: vyberte státní občanství`),
        otherwise: schema => schema.nullable()
    }),
    birthdate: yup.mixed().when(['isPolicyHolder', 'isInsuredPerson', 'isForeigner'], {
        is: (isPolicyHolder: boolean, isInsuredPerson: boolean, isForeigner: boolean) =>
            otherPersonCondition(isPolicyHolder, isInsuredPerson) && !!isForeigner,
        // required() u mixed propustí prázdný řetězec (datepicker při smazání hodnoty nastavuje '')
        then: schema => schema.test('birthdate-required',
            ({ path }) => `${personLabel(path)}: zadejte datum narození`,
            value => !!value),
        otherwise: schema => schema.nullable()
    })
})

// Chytré cestovko: 1 a 2 osoby se zadávají přesně, rodina max 7 osob a z toho max 2 osoby starší 18 let.
// Kontroly počtu jsou navěšené na insuredCount, aby v souhrnu chyb nekolidovaly s chybami jednotlivých osob
export const SmartTravelValidationSchema = yup.object().shape({
    insuredCount: yup.mixed()
        .test('smart-travel-persons-count', function () {
            const insuredCount = Number(this.parent?.insuredCount)
            const personsCount = (this.parent?.insuredPersons ?? []).length

            if (insuredCount == 1 && personsCount != 1)
                return this.createError({ message: 'Chytré cestovko je sjednáno pro 1 osobu, zadejte právě jednu pojištěnou osobu' })

            if (insuredCount == 2 && personsCount != 2)
                return this.createError({ message: 'Chytré cestovko je sjednáno pro 2 osoby, zadejte právě dvě pojištěné osoby' })

            if (insuredCount == 3 && (personsCount < 3 || personsCount > 7))
                return this.createError({ message: 'Chytré cestovko pro rodinu umožňuje zadat 3 až 7 pojištěných osob' })

            return true
        })
        .test('smart-travel-adults-count', 'Chytré cestovko pro rodinu umožňuje pojistit maximálně 2 osoby starší 26 let', function () {
            if (Number(this.parent?.insuredCount) != 3) return true

            const persons = (this.parent?.insuredPersons ?? []) as Partial<SmartTravelInsuredPerson>[]
            const adultsCount = persons
                .filter(p => !!p.birthnumber || !!p.birthdate)
                .filter(isAdult)
                .length

            return adultsCount <= 2
        }),
    insuredPersons: yup.array().of(SmartTravelInsuredPersonValidationSchema)
}) as any
